import { Info } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchJson } from "../../api/rest";
import type { AdsResult } from "../../types/planner";
import { ProductRow } from "./ProductRow";

const DAY_OPTIONS = [7, 30, 90];

/**
 * True per-product Partner-ads attribution (§1.3's `uid`) doesn't exist yet,
 * so this renders whatever the server hands back — `mode` and `since` say
 * plainly which kind of ranking is showing, and an empty `items` list is a
 * normal, valid state here, never an error.
 */
export const AdsTab = () => {
	const [days, setDays] = useState(30);
	const [result, setResult] = useState<AdsResult | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		fetchJson<AdsResult>(`/planner/ads?days=${days}`)
			.then((r) => {
				if (cancelled) return;
				setResult(r);
				setError(null);
			})
			.catch((e: Error) => {
				if (cancelled) return;
				// Even a failed fetch must not blank the tab — fall back to the
				// same empty-but-valid shape the server itself degrades to.
				setResult({ mode: "merchant", since: null, items: [] });
				setError(e.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [days]);

	const bannerText =
		result?.mode === "product"
			? `True per-product Partner-ads attribution${result.since ? `, since ${new Date(result.since).toLocaleDateString("da-DK")}` : ""}.`
			: `Merchant-level proxy — per-product conversions aren't wired up yet, so these are the best products from the merchants Partner-ads is converting for${result?.since ? `, since ${new Date(result.since).toLocaleDateString("da-DK")}` : ""}.`;

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2 text-xs text-info">
					<Info className="h-4 w-4 shrink-0" />
					{bannerText}
				</div>
				<select
					value={days}
					onChange={(e) => setDays(Number(e.target.value))}
					className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm"
				>
					{DAY_OPTIONS.map((d) => (
						<option key={d} value={d}>
							Last {d} days
						</option>
					))}
				</select>
			</div>

			{error && (
				<p className="text-xs text-slate-400">
					Ranking service returned an error ({error}); showing an empty list.
				</p>
			)}

			<div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
							<tr>
								<th className="px-4 py-2.5 font-medium">Product</th>
								<th className="px-4 py-2.5 font-medium">Merchants</th>
								<th className="px-4 py-2.5 font-medium">Price</th>
								<th className="px-4 py-2.5 font-medium">Content</th>
								<th className="px-4 py-2.5 font-medium">Rating</th>
								<th className="px-4 py-2.5 font-medium">Attributes</th>
								<th className="px-4 py-2.5 font-medium">CSS</th>
								<th className="px-4 py-2.5 font-medium">Traffic</th>
								<th className="px-4 py-2.5 font-medium">Conversions</th>
							</tr>
						</thead>
						<tbody>
							{loading && (
								<tr>
									<td
										colSpan={9}
										className="px-4 py-8 text-center text-slate-400"
									>
										Loading…
									</td>
								</tr>
							)}
							{!loading && (result?.items.length ?? 0) === 0 && (
								<tr>
									<td
										colSpan={9}
										className="px-4 py-8 text-center text-slate-400"
									>
										No ads-attributed products yet in this window.
									</td>
								</tr>
							)}
							{result?.items.map((row) => (
								<ProductRow key={row.productId} row={row} showConversions />
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};
